/**
 * «کلیکِ بیرون یا Escape می‌بنده» — قاعده‌ی هر کادرِ شناوری توی این اپ.
 *
 * از داخلِ DiscoverPage در اومد بیرون وقتی دکمه‌ی شهر هم همینو خواست. دو تا
 * کپیِ همین چند خط از قبل توی اپ بود (RespondMenu و DiscoverPage) و سومی
 * دیگه زیادی بود.
 */
import { useEffect, type RefObject } from 'react';

export function useOutsideClose(
  ref: RefObject<HTMLElement | null>, open: boolean, close: () => void,
): void {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      /**
       * ردیفی که با همین کلیک از صفحه برداشته شده، «بیرونِ کادر» نیست.
       *
       * این‌جا دو تا شنونده روی یه رویداد سوارن: خودِ React (که کلیک رو
       * می‌گیره و همون لحظه لیست رو می‌بنده) و این یکی، که بعدِ اون اجرا
       * می‌شه. تا نوبت به این برسه، ردیفی که کاربر روش کلیک کرده دیگه توی
       * صفحه نیست — و `contains` روی یه گرهِ جدا شده همیشه false است.
       *
       * نتیجه‌ش یه باگِ واقعی بود: توی فرمِ «شهرم این‌جا نیست»، انتخابِ کشور
       * کلِ کادر رو می‌بست و کاربر باید از اول شروع می‌کرد.
       */
      if (target && !target.isConnected) return;
      if (ref.current?.contains(target)) return;
      close();
      /* استثنا: دکمه‌ای که خودش کادرِ شناورِ دیگری را باز می‌کند (فیلتر ↔
         ترتیب ↔ شهر). آن‌جا کاربر دارد یکی را جای دیگری می‌گذارد، نه اینکه
         تصادفی جای دیگری بزند — و دو کلیک خواستن از او فقط دکمه را مرده
         نشان می‌دهد. */
      const el = target instanceof Element ? target : target?.parentElement ?? null;
      if (el?.closest('[aria-haspopup]')) return;
      /**
       * این کلیک فقط «ببند» است و بس — نباید زیرِ کادر هم کاری بکند.
       *
       * `mousedown` کادر را می‌بندد، ولی `click` بعدش همچنان به هر چیزی که
       * زیرِ انگشت بوده می‌رسد: کاربر می‌خواست فیلتر را ببندد و یک رویداد
       * باز می‌شد. پس همان یک کلیک را در فازِ capture (پیش از React) می‌بلعیم.
       *
       * خودش را برمی‌دارد: یا با همان کلیک (`once`)، یا اگر کلیکی نیامد
       * (کشیدن و رها کردن بیرونِ پنجره) با `mousedown`ِ بعدی.
       */
      const swallow = (ev: MouseEvent) => { ev.stopPropagation(); ev.preventDefault(); };
      const drop = () => document.removeEventListener('click', swallow, true);
      document.addEventListener('click', swallow, { capture: true, once: true });
      document.addEventListener('mousedown', drop, { capture: true, once: true });
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  });
}
